import React, { useMemo } from 'react';
import { ABORTED, ABORTED_STATUS, COMPLETED, COMPLETED_STATUS, DRAFT, DRAFT_STATUS, IN_PROGRESS, IN_PROGRESS_STATUS, INITIATED, INITIATED_STATUS, SCHEDULED, SCHEDULED_STATUS } from '../utils/Constants';
import { ABTestStatus } from '../types/ab';
import Status from 'Components/DesignSystem/Status/Status';
import { StatusTone } from 'types/DesignSystem/Status';

type TCustomChip = {
    status: ABTestStatus | string | boolean | React.JSX.Element;
    /** Forwarded to the DS `Status`. Also names its `-chip` and `-label` children. */
    'data-testid'?: string;
}

/**
 * The A/B test status chip — now a mapping onto the design system's `Status`, not a
 * chip of its own (`okf/design-system/e2e.md` § 138).
 *
 * **Every displayed string is unchanged**, and so is the overloaded `status` prop: a
 * status constant, a boolean, an arbitrary string, or a JSX element. Three call sites
 * depend on all four shapes (`ABTable`, `ResultTable`, `ABResultHeader`), so the
 * signature stays exactly as it was and only the rendering moves.
 *
 * **Two real bugs went with the old implementation.** It computed a `textColor` string
 * and applied it as `style={{ color: textColor }}` — but two branches assigned Tailwind
 * class names, `'text-white'` and `'text-black'`. Neither is a valid CSS colour, so the
 * browser discarded the declaration and both chips rendered in whatever colour they
 * inherited. That hit the boolean-`true` "Reached" chip and, more visibly, the DEFAULT
 * branch — which is what the "Promoted" chip and the results-page countdown both use.
 *
 * **What changes visually.** Draft / Initiated / Scheduled were a white chip with a
 * `#E5E5EA` border; they are now the `neutral` tint, so all six states read as chips of
 * one family instead of three of them looking like empty outlines. Every tone pair here
 * is measured against its own tint in `Status` — the legacy pairs never were.
 */

/** Legacy status constant → design-system tone. Copy comes from `utils/Constants`. */
const STATUS_TONES: Record<string, { tone: StatusTone; label: string }> = {
    [DRAFT_STATUS]: { tone: 'neutral', label: DRAFT },
    [INITIATED_STATUS]: { tone: 'neutral', label: INITIATED },
    [SCHEDULED_STATUS]: { tone: 'neutral', label: SCHEDULED },
    [ABORTED_STATUS]: { tone: 'danger', label: ABORTED },
    [IN_PROGRESS_STATUS]: { tone: 'warning', label: IN_PROGRESS },
    [COMPLETED_STATUS]: { tone: 'success', label: COMPLETED },
};

const CustomChip = ({ status, 'data-testid': dataTestId }: TCustomChip) => {

    const { tone, label } = useMemo((): { tone: StatusTone; label: React.ReactNode } => {
        if (typeof status === 'boolean') {
            /*
              The MLP reached/not-reached chip. "Reached" was a white chip whose ink
              never applied (see the docblock); `success` gives it the meaning the copy
              already carried.
            */
            return status
                ? { tone: 'success', label: 'Reached' }
                : { tone: 'neutral', label: 'Not Reached' };
        }

        if (typeof status === 'string' && STATUS_TONES[status]) {
            return STATUS_TONES[status];
        }

        /*
          The catch-all: an arbitrary string ("Promoted") or a JSX element (the results
          header's "N days left in the test"). It was `#dadbdd` with `rounded-xl` — the
          only rounding in the set that differed, which made "Promoted" a visually
          distinct KIND of thing from the status beside it rather than a quieter one.
          `neutral` is the same #dadbdd ground at the shared control radius.
        */
        return { tone: 'neutral', label: status };
    }, [status]);

    return <Status tone={tone} label={label} data-testid={dataTestId} />;
}

export default CustomChip;
